import {
    findImportDraftByAnalysisProgressId,
    isImportDraftId,
} from "./importDraftProgress.js";

/**
 * Resolve the server progress id for the active import wizard session.
 *
 * @param {{
 *   persistedState?: object|null,
 *   currentProgressId?: number|string|null,
 * }} options
 * @returns {number|null}
 */
export function resolveActiveImportProgressId({
    persistedState = null,
    currentProgressId = null,
} = {}) {
    const candidates = [
        persistedState?.analysisResults?.progress_id,
        persistedState?.currentProgressId,
        persistedState?.jiraProgressId,
        persistedState?.asanaProgressId,
        persistedState?.clickupProgressId,
        currentProgressId,
        window.jiraProgressId,
        window.asanaProgressId,
        window.clickupProgressId,
    ];

    for (const id of candidates) {
        if (id == null || id === "") {
            continue;
        }

        if (isImportDraftId(id)) {
            continue;
        }

        const numericId = Number(id);

        if (!Number.isNaN(numericId) && numericId > 0) {
            return numericId;
        }
    }

    return null;
}

/**
 * True when Operations already has a non-draft row for this progress id.
 *
 * Analysis progress ids are reused when the real import starts, so discard
 * must not tear down that live job (or POST discard against a still-pending import).
 *
 * @param {Array<{ id?: string|number }>|null|undefined} progresses
 * @param {string|number|null|undefined} progressId
 * @returns {boolean}
 */
export function isLiveImportProgressId(progresses, progressId) {
    if (progressId == null || progressId === "") {
        return false;
    }

    const key = String(progressId);

    return (progresses ?? []).some(
        (progress) =>
            String(progress?.id) === key && !isImportDraftId(progress?.id),
    );
}

/**
 * Whether leaving the import wizard should discard server-side analysis.
 * Saved or resumed drafts keep their linked analysis progress on the server.
 *
 * @param {{
 *   progressId?: number|string|null,
 *   initiatedProgresses?: Array<{ id?: string|number, meta?: { draft_state?: object } }>,
 * }} options
 * @returns {boolean}
 */
export function shouldDiscardServerAnalysisOnLeaveImport({
    progressId = null,
    initiatedProgresses = [],
} = {}) {
    if (progressId == null || progressId === "") {
        return false;
    }

    if (
        findImportDraftByAnalysisProgressId(initiatedProgresses, progressId)
    ) {
        return false;
    }

    return true;
}
